import { ChevronUp, Lock, Megaphone, ShieldOff } from 'lucide-react';
import { type CSSProperties, useEffect, useMemo, useState } from 'react';
import { useBlocker, useNavigate, useSearchParams } from 'react-router';
import type { ComposeDraft, ComposeDraftSummary, Conversation } from '../../api/types';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { RefreshButton } from '../../components/RefreshButton';
import {
  CHANNEL_META,
  CONVERSATION_KEYS,
  channelKeyForInbox,
  hasVoiceInbox,
  type ChannelKey,
} from '../../lib/commsMeta';
import { CallBar } from './CallBar';
import { CallsView } from './CallsView';
import { IncomingCallOverlay } from './IncomingCallOverlay';
import { LiveCallsProvider } from './liveCalls';
import { useCallLive } from './liveCallFlag';
import { useOverlayPosition } from './overlayDrag';
import { CampaignsView } from './CampaignsView';
import { ChannelBarContext } from './channelBarContext';
import { CommsProvider } from './CommsProvider';
import { DialerPanel } from './DialerPanel';
import { DialerProvider } from './DialerProvider';
import { ComposePanel, type ComposeSeed } from './ComposePanel';
import { useComms } from './context';
import { useReach } from './useReach';
import { InboxView } from './InboxView';
import { NotificationsBell } from './NotificationsBell';
import { NotificationToaster } from './NotificationToaster';
import { SettingsView } from './SettingsView';
import { NotificationsProvider } from './NotificationsProvider';
import { PhonePanel } from './PhonePanel';
import { PhoneProvider } from './PhoneProvider';
import { AnalyticsView } from '../analytics/AnalyticsView';
import './comms.css';

const CONVERSATION_TABS: ChannelKey[] = ['all', ...CONVERSATION_KEYS];

export function CommsModule() {
  const {
    inboxes,
    inboxesLoading,
    accessDenied,
    sendDenied,
    noChannelAccess,
    callsAvailable,
    refreshAll,
    refreshingAll,
    source,
    capabilities,
  } = useComms();
  // Permissive while /meta is in flight, like every other capability read here:
  // the server refuses what the agent may not see, and a slow lookup must not
  // make a tab flicker in and out of the strip.
  const analyticsAvailable = (capabilities?.access.features.analytics ?? 'view') !== 'off';
  const [params, setParams] = useSearchParams();
  const [composeOpen, setComposeOpen] = useState(false);
  /**
   * An unsent new message reopened from the Drafts view. Held here, beside the
   * panel it fills, because that panel is docked over the whole module and
   * survives every tab switch — which is the only reason an agent can look
   * something up while writing.
   *
   * The FULL draft, fetched on the click: the list row carries a one-line
   * summary, and putting a body back in a box needs the body.
   */
  const [composeDraft, setComposeDraft] = useState<ComposeDraft | null>(null);
  const [builderNonce, setBuilderNonce] = useState(0);

  const tabs = useMemo<ChannelKey[]>(() => {
    const present = new Set(inboxes.map(channelKeyForInbox));
    return [
      'all',
      ...CONVERSATION_KEYS.filter((key) => present.has(key)),
      // Voice has to exist AND be granted: call records are content, so an agent
      // without calling anywhere sees no Calls tab rather than an empty one.
      ...(hasVoiceInbox(inboxes) && callsAvailable ? (['calls'] as const) : []),
      'campaigns',
      // Embed only. In the standalone console Analytics is a tab of the
      // Dashboard section, so offering it here too would put the same screen in
      // the chrome twice. An older dashboard has no such chrome — the module IS
      // its Communications page — so the tab strip is the only place it can
      // hang; one that says `websiteHost` frames it under My website instead,
      // and there it is not a part of the inbox.
      ...(source.embedded && !source.websiteHost && analyticsAvailable ? (['analytics'] as const) : []),
      'settings',
    ];
  }, [inboxes, callsAvailable, source.embedded, source.websiteHost, analyticsAvailable]);

  const rawTab = params.get('tab') as ChannelKey | null;
  const tab: ChannelKey = rawTab && tabs.includes(rawTab) ? rawTab : 'all';
  const selectedId = params.get('c') ? Number(params.get('c')) : null;

  /**
   * Is the channel bar — the All / Email / WhatsApp row and the actions beside
   * it — showing?
   *
   * `?tabs=collapsed` is the dashboard asking for it folded away, because the
   * shell it frames this module in already carries the same choices and two of
   * those stacked is one too many; `expanded` asks for it back, which is what
   * the unified inbox asks for, the bar being the navigation there.
   *
   * Folding gives the bar's HEIGHT back — 48px to the list and the thread, on a
   * page that has already spent some on the dashboard's own chrome. That is
   * what makes it worth doing and what decides the shape: everything in the bar
   * goes, the actions included, leaving a sliver that says which tab is open
   * and takes one click to undo.
   *
   * A default, not a lock. It is applied when it CHANGES rather than read on
   * every render, so the fold button really does decide: an agent who opens the
   * reel keeps it through every tab and conversation they click afterwards,
   * because those rewrite the query and would otherwise re-assert a parameter
   * the agent has already answered. Only the parent saying something DIFFERENT
   * — arriving at All from a channel shortcut, say — moves it again.
   */
  const reelParam = params.get('tabs');
  const [reelOpen, setReelOpen] = useState(reelParam !== 'collapsed');
  const [appliedReelParam, setAppliedReelParam] = useState(reelParam);
  if (reelParam !== appliedReelParam) {
    // Adjusted during render rather than in an effect, the way React documents
    // resetting state from a changed input: the strip has to be right in the
    // frame the new parameter arrives in, and an effect would paint the old
    // answer first.
    setAppliedReelParam(reelParam);
    if (reelParam !== null) setReelOpen(reelParam !== 'collapsed');
  }

  const channelBar = useMemo(
    () => ({ folded: !reelOpen, show: () => setReelOpen(true) }),
    [reelOpen],
  );

  const setTab = (next: ChannelKey) => {
    setParams((current) => {
      const draft = new URLSearchParams(current);
      draft.set('tab', next);
      draft.delete('c');
      // Where the acquisition card opened is for the visit that asked, not for
      // every later return to the Analytics tab.
      draft.delete('acq');
      return draft;
    });
  };

  /**
   * Campaigns ↔ Analytics, both ways. A campaign's Results (and the Guide) open
   * the Analytics Campaigns card — the module's own tab where it has one, which
   * is the embed, and the Dashboard's otherwise; a Campaigns row a campaign's
   * tracked links produced opens that campaign here. Neither is offered where
   * the other end is out of reach: My Website frames Analytics with no inbox.
   */
  const navigate = useNavigate();
  const openAnalytics = tabs.includes('analytics')
    ? () =>
        setParams((current) => {
          const draft = new URLSearchParams(current);
          draft.set('tab', 'analytics');
          draft.set('acq', 'campaigns');
          draft.delete('c');
          return draft;
        })
    : !source.embedded && analyticsAvailable
      ? () => navigate('/dashboard/analytics?acq=campaigns')
      : undefined;
  const openCampaign = (id: number) =>
    setParams((current) => {
      const draft = new URLSearchParams(current);
      draft.set('tab', 'campaigns');
      draft.set('campaign', String(id));
      draft.delete('acq');
      draft.delete('c');
      return draft;
    });

  const setSelectedId = (id: number | null) => {
    setParams((current) => {
      const draft = new URLSearchParams(current);
      if (id === null) draft.delete('c');
      else draft.set('c', String(id));
      return draft;
    });
  };

  // Where the floating call overlay sits — dragged by the agent, remembered
  // by this browser; null is the corner the stylesheet puts it in.
  const overlayAt = useOverlayPosition();

  const openConversation = (id: number) => {
    setParams((current) => {
      const draft = new URLSearchParams(current);
      draft.set('tab', 'all');
      draft.set('c', String(id));
      return draft;
    });
  };

  // `?to=<phone|email>` — a person the dispatch dashboard named by address,
  // from a call, mail or SMS icon on a reservation row or a job page. It
  // resolves to their conversation, or to a compose window opened on them.
  const { seed, clearSeed } = useReach(params.get('to'), rawTab, {
    openConversation: (id, channelKey) => {
      setParams((current) => {
        const draft = new URLSearchParams(current);
        draft.set('tab', tabs.includes(channelKey) ? channelKey : 'all');
        draft.set('c', String(id));
        // One write, not two: the request is spent the moment it is answered.
        draft.delete('to');
        return draft;
      });
    },
    clear: () => {
      setParams((current) => {
        const draft = new URLSearchParams(current);
        draft.delete('to');
        return draft;
      });
    },
  });

  // A resolution that found no thread opens the composer ON that person.
  // Derived, not copied into state: while the seed stands it IS the open
  // panel, so there is no second copy to fall out of step with it.
  const composeSeed: ComposeSeed | null =
    seed === null
      ? null
      : {
          address: seed.intent.to,
          isEmail: seed.intent.isEmail,
          channelKey: seed.intent.channelKey,
          contact: seed.contact,
        };
  const showCompose = composeOpen || composeSeed !== null || composeDraft !== null;

  /**
   * A blank composer. Blank is the point: neither the recipient a dashboard icon
   * seeded nor the draft reopened from the Drafts view may still be in the box
   * when the agent asks for a NEW message, so both are spent before the panel
   * opens. Displacing a draft is safe — the panel saves what it holds on the way
   * out, so it is waiting in Drafts rather than gone.
   */
  const openCompose = () => {
    clearSeed();
    setComposeDraft(null);
    setComposeOpen(true);
  };

  const closeCompose = () => {
    clearSeed();
    setComposeOpen(false);
    setComposeDraft(null);
  };

  /**
   * Reopen an unsent new message into the dock.
   *
   * There is one dock, so this REPLACES whatever is in it — safe only because
   * the panel saves what it holds on the way out (see its unmount flush): the
   * message being displaced becomes a draft in the same list the agent just
   * clicked in, rather than disappearing. Reopening the one already open does
   * nothing at all, or a click on the row you are writing in would refetch the
   * body from before your last few words.
   */
  const openComposeDraft = (summary: ComposeDraftSummary) => {
    if (composeDraft?.id === summary.id) return;
    source.api.getComposeDraft(summary.id).then(
      (full) => {
        clearSeed();
        setComposeOpen(false);
        setComposeDraft(full);
      },
      () => {
        // Gone, or the channel is no longer theirs to send on. The Drafts view
        // corrects itself on its next read; opening a window on a draft that
        // does not exist would be the worse answer.
      },
    );
  };

  const onComposeCreated = (conversation: Conversation, channelKey: ChannelKey) => {
    closeCompose();
    setParams((current) => {
      const draft = new URLSearchParams(current);
      draft.set('tab', tabs.includes(channelKey) ? channelKey : 'all');
      draft.set('c', String(conversation.id));
      return draft;
    });
  };

  if (accessDenied) {
    return (
      <div className="comms">
        <div className="comms-locked">
          <div className="comms-locked-inner">
            <div className="comms-locked-icon">
              <ShieldOff size={28} />
            </div>
            <div className="comms-locked-title">No access to Communications</div>
            <div className="comms-locked-body">
              Your dashboard role does not have the <b>crm_all</b> permission. Ask an admin to
              add it in dispatch role settings — the module unlocks on your next sign-in.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    // The softphone lives HERE rather than at each route, because the module is
    // mounted from three places — the standalone screen, the dashboard iframe
    // and the preview — and one that forgot the provider would simply never ring
    // with no error to show for it. Mounting it twice would be worse still: two
    // Devices on one identity both ring, and race to answer.
    <PhoneProvider>
      {/* The RingCentral dialer sits at the same level and for the same reason:
          it has to be registered BEFORE a call arrives to be able to answer it,
          so it outlives every tab change rather than living on the Calls tab. */}
      <DialerProvider>
        <NotificationsProvider selectedId={selectedId} onOpenConversation={openConversation}>
          <LiveCallsProvider>
          {/* Every view below renders its own way back to a folded bar — see
              [ShowChannelBar]. Memoised on the two things it carries, or each
              of those views re-renders on every keystroke this component sees. */}
          <ChannelBarContext.Provider value={channelBar}>
          <div
            className={`comms${overlayAt ? ' ov-moved' : ''}`}
            style={overlayAt ? overlayVars(overlayAt) : undefined}
          >
            {/* Above the tabs on purpose: a call outlives whichever tab is open, and
              an Answer button that scrolls away with a thread is a missed call. */}
            <CallBar onOpenConversation={openConversation} />
            {/* Folded, the bar is not rendered at all — every one of its 48px
                goes to the list and the thread, which is the only reason to fold
                it. What brings it back is [ShowChannelBar], rendered by whatever
                view is showing (the inbox rail, the campaigns sub-nav, the calls
                filter bar, the settings page): a sliver kept here to hold the
                button would have spent a third of what folding saves. */}
            {reelOpen && (
              <div className="comms-tabs">
                  {/* At the head of the bar rather than after New campaign,
                      where it read as part of that button group rather than as
                      chrome belonging to the bar itself. */}
                  <button
                    type="button"
                    className="comms-tabs-fold"
                    aria-expanded
                    title="Hide the channel bar"
                    onClick={() => setReelOpen(false)}
                  >
                    <ChevronUp size={15} />
                  </button>
                  {/* Only the channel tabs scroll. The actions beside them —
                      Refresh above all — must stay reachable at any width:
                      embedded in the dashboard the module is as wide as that
                      page leaves it, and a refresh you have to scroll a strip
                      sideways to find is one the agent does not have. */}
                  <div className="comms-tabs-scroll">
                    {tabs.map((key) => {
                      const meta = CHANNEL_META[key];
                      const Icon = meta.icon;
                      const active = tab === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          className={`comms-tab${active ? ' active' : ''}`}
                          style={
                            active
                              ? { background: meta.muted, borderColor: meta.color, color: meta.color }
                              : undefined
                          }
                          onClick={() => setTab(key)}
                        >
                          <Icon size={15} style={{ color: active ? meta.color : 'var(--c-muted)' }} />
                          {meta.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="comms-tabs-spacer" />
                  {/* Refreshes the whole module, not the tab you happen to be on —
                      and it is the ONLY way to do that inside the dashboard iframe,
                      where there is no address bar to reload from. */}
                  <RefreshButton
                    className="comms-tab-action icon"
                    size={15}
                    label={null}
                    title="Refresh Communications"
                    refreshing={refreshingAll}
                    onClick={refreshAll}
                  />
                  <NotificationsBell />
                  {/* Compose is NOT here. It moved into the inbox rail, above
                      the mailbox list, because this bar folds away — and the
                      dashboard folds it by default, which took the one action
                      that starts work with it. New campaign stays: campaigns
                      are their own tab, and the rail belongs to the inbox. */}
                  <button
                    type="button"
                    className="comms-tab-action primary"
                    disabled={sendDenied}
                    title={sendDenied ? 'Your role cannot send campaigns' : undefined}
                    onClick={() => {
                      setTab('campaigns');
                      setBuilderNonce((nonce) => nonce + 1);
                    }}
                  >
                    <Megaphone size={15} />
                    New campaign
                  </button>
              </div>
            )}

            {noChannelAccess ? (
              <div className="comms-readonly">
                <Lock size={15} />
                <span>
                  <b>No channel access.</b> You can open Communications, but no channel has been
                  granted to you — an admin gives you one under Settings → Users &amp; access.
                </span>
              </div>
            ) : sendDenied ? (
              <div className="comms-readonly">
                <Lock size={15} />
                <span>
                  <b>Read-only access.</b> You can view conversations but not reply, call out or
                  send campaigns — every channel you can see is set to <i>View</i>. An admin raises
                  that to <i>Write</i> under Settings → Users &amp; access.
                </span>
              </div>
            ) : null}

            {inboxesLoading && inboxes.length === 0 ? (
              <div className="comms-locked">
                <div className="comms-spin">Loading channels…</div>
              </div>
            ) : CONVERSATION_TABS.includes(tab) ? (
              <InboxView
                channelKey={tab}
                selectedId={selectedId}
                onSelectId={setSelectedId}
                onCompose={openCompose}
                onOpenComposeDraft={openComposeDraft}
              />
            ) : tab === 'calls' ? (
              <CallsView onOpenConversation={openConversation} />
            ) : tab === 'campaigns' ? (
              <CampaignsView builderNonce={builderNonce} onOpenAnalytics={openAnalytics} />
            ) : tab === 'analytics' ? (
              <AnalyticsView
                api={source.api}
                embedded
                initialAcqTab={params.get('acq') === 'campaigns' ? 'campaigns' : undefined}
                onOpenCampaign={openCampaign}
              />
            ) : (
              <SettingsView />
            )}

            {/* Rendered outside the tab body, so switching tabs or opening
                another conversation leaves the draft standing. Two things key
                it, and both mean "a different message": WHO it is addressed to,
                so a second contact icon in the dashboard starts over on that
                person (pressing the same one twice keeps the draft), and WHICH
                stored draft was reopened from the Drafts view. */}
            {showCompose && (
              <ComposePanel
                key={composeDraft ? `draft-${composeDraft.id}` : (composeSeed?.address ?? 'blank')}
                seed={composeSeed ?? undefined}
                draft={composeDraft ?? undefined}
                activeChannelKey={tab}
                onClose={closeCompose}
                onCreated={onComposeCreated}
              />
            )}
            <NotificationToaster />
            <IncomingCallOverlay onOpenConversation={openConversation} />
            <DialerPanel />
            <PhonePanel />
          </div>
          </ChannelBarContext.Provider>
          </LiveCallsProvider>
        </NotificationsProvider>
      </DialerProvider>
    </PhoneProvider>
  );
}


/**
 * Leaving Communications unmounts the module and, with it, the softphone —
 * and destroying a Twilio device disconnects every call it holds. Asked here
 * rather than inside the phone because this is the ROUTE's own question, and
 * the route is the only place that can still answer no.
 *
 * Standalone only, like this route: embedded, the dashboard keeps the phone in
 * a frame that moving around cannot reach.
 */
function CallNavigationGuard() {
  const live = useCallLive();
  const blocker = useBlocker(live);

  // Closing or reloading the tab loses the same call, and the browser's own
  // dialog is the only one available for it.
  useEffect(() => {
    if (!live) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [live]);

  // The call ending while the question is on screen answers it: there is
  // nothing left to lose, so the agent goes where they were going.
  useEffect(() => {
    if (blocker.state === 'blocked' && !live) blocker.proceed();
  }, [blocker, live]);

  if (blocker.state !== 'blocked') return null;
  return (
    <ConfirmDialog
      title="Leave this call?"
      body={
        <p>
          You are on a call. Leaving Communications <strong>hangs up on the other person</strong>.
        </p>
      }
      confirmLabel="Leave and hang up"
      danger
      onConfirm={() => blocker.proceed()}
      onCancel={() => blocker.reset()}
    />
  );
}

/** Route element: provider + module. */
export function CommsScreen() {
  return (
    <CommsProvider>
      <CallNavigationGuard />
      <CommsModule />
    </CommsProvider>
  );
}

/** The dragged anchor as the CSS variables every floating piece reads (comms.css `.ov-moved`). */
function overlayVars(point: { x: number; y: number }): CSSProperties {
  return { '--ov-x': `${point.x}px`, '--ov-y': `${point.y}px` } as CSSProperties;
}
